import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  useActionItems,
  type ActionCategory,
  type ActionState,
} from "@/hooks/use-action-items";
import { useTaskAction } from "@/hooks/use-task-action";
import { TaskListView } from "@/components/tasks/TaskListView";
import { TaskKanbanView } from "@/components/tasks/TaskKanbanView";
import { TaskFilters } from "@/components/tasks/TaskFilters";
import { TaskDetailDrawer } from "@/components/tasks/TaskDetailDrawer";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { formatError } from "@/lib/format-error";

/**
 * BW19 PR-E — /tasks route.
 *
 * Toggle between list (dense, due-sorted) and kanban (open / in_progress
 * / blocked / done). Click a card to open the detail drawer with inline
 * editing for state / category / due_at / scheduled_at / effort.
 */

type ViewMode = "list" | "kanban";

export function Tasks() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const [view, setView] = useState<ViewMode>("list");
  const [showDone, setShowDone] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<Set<ActionCategory>>(new Set());
  const [projectFilter, setProjectFilter] = useState<Set<string>>(new Set());
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  // BW20.1 PR-C: useActionItems' includeDone flag included done but
  // excluded cancelled. We need both visibility toggles independently.
  const { data, isLoading, error } = useActionItems({
    includeDone: showDone,
    includeCancelled: showCancelled,
  });
  const taskAction = useTaskAction();

  const projectsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ["task_filter_projects", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data: rows, error: err } = await supabase
        .from("project")
        .select("id, name")
        .eq("user_id", userId)
        .order("name");
      if (err) throw err;
      return rows ?? [];
    },
  });
  const projects = projectsQuery.data ?? [];

  const items = useMemo(() => {
    if (!data) return [];
    return data.filter((it) => {
      if (categoryFilter.size > 0 && !categoryFilter.has(it.category)) return false;
      if (projectFilter.size > 0) {
        if (!it.project_id) return false;
        if (!projectFilter.has(it.project_id)) return false;
      }
      return true;
    });
  }, [data, categoryFilter, projectFilter]);

  function toggleProject(id: string) {
    setProjectFilter((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleCategory(c: ActionCategory) {
    setCategoryFilter((curr) => {
      const next = new Set(curr);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }

  function changeState(id: string, state: ActionState) {
    const item = data?.find((it) => it.id === id);
    if (!item) return;
    void taskAction.onCycleState(item, state);
  }
  function cancelTask(id: string) {
    const item = data?.find((it) => it.id === id);
    if (!item) return;
    void taskAction.onCancel(item);
  }

  return (
    <article aria-labelledby="tasks-heading">
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-4)",
        }}
      >
        <h1 id="tasks-heading" style={{ margin: 0, fontSize: 22, color: "var(--fg)" }}>
          Tasks
        </h1>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "inline-flex", gap: 4 }}>
            <button
              type="button"
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
              style={tabBtn(view === "list")}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setView("kanban")}
              aria-pressed={view === "kanban"}
              style={tabBtn(view === "kanban")}
            >
              Kanban
            </button>
          </div>
          <button
            type="button"
            onClick={() => setNewTaskOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              background: "var(--accent)",
              color: "var(--accent-on, #0a0b0d)",
              border: "none",
              borderRadius: "var(--radius-card)",
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            <Plus size={14} strokeWidth={1.8} />
            New task
          </button>
        </div>
      </header>

      <div style={{ marginBottom: "var(--space-4)", display: "grid", gap: 8 }}>
        <TaskFilters
          categories={categoryFilter}
          onToggleCategory={toggleCategory}
          showDone={showDone}
          onToggleShowDone={() => setShowDone((v) => !v)}
          showCancelled={showCancelled}
          onToggleShowCancelled={() => setShowCancelled((v) => !v)}
        />
        {projects.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Project
            </span>
            {projects.map((p) => {
              const active = projectFilter.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProject(p.id)}
                  aria-pressed={active}
                  style={{
                    padding: "6px 10px",
                    background: active ? "var(--chip-hi, var(--chip))" : "var(--chip)",
                    color: "var(--fg)",
                    border: `1px solid ${active ? "var(--accent, var(--fg))" : "var(--hair)"}`,
                    borderRadius: "var(--radius-card)",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {isLoading && <p style={{ color: "var(--fg-2)" }}>Loading tasks...</p>}
      {error && <p style={{ color: "var(--danger, #c53030)" }}>{formatError(error)}</p>}

      {!isLoading && !error && view === "list" && (
        <TaskListView
          items={items}
          onCycleState={changeState}
          onCancel={cancelTask}
          onOpen={setOpenTaskId}
        />
      )}
      {!isLoading && !error && view === "kanban" && (
        <TaskKanbanView
          items={items}
          onCycleState={changeState}
          onCancel={cancelTask}
          onMove={changeState}
          onOpen={setOpenTaskId}
        />
      )}

      <TaskDetailDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
      <NewTaskDialog open={newTaskOpen} onClose={() => setNewTaskOpen(false)} />
    </article>
  );
}

function tabBtn(active: boolean): React.CSSProperties {
  return {
    padding: "6px 12px",
    background: active ? "var(--chip-hi, var(--chip))" : "transparent",
    color: "var(--fg)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-card)",
    fontSize: 13,
    cursor: "pointer",
  };
}
